/**
 * Module dependencies.
 */

import debug from "debug";
import Emitter from "component-emitter";
import { array as isArray, object as isObject, string as isString } from "is";
import nextTick from "next-tick";
import normalize from "to-no-case";

import loadScript from "./loadScript";

/**
 * hasOwnProperty reference.
 */

const has = Object.prototype.hasOwnProperty;

/**
 * No operation.
 */

const noop = function noop() {};

/**
 * Window defaults.
 */

const onerror = window.onerror;
const onload = null;

/**
 * Determine the type of the option passed to `#map`
 *
 * @api private
 * @param {Object|Object[]} mapping
 * @return {String} mappingType
 */

function getMappingType(mapping) {
  if (isArray(mapping)) {
    for (const i in mapping) {
      if (has.call(mapping, i) && !isMixed(mapping[i])) {
        return "array";
      }
    }

    return "mixed";
  }
  if (isObject(mapping)) {
    return "map";
  }
  return "unknown";
}

/**
 * Determine if item in mapping array is a valid 'mixed' type value
 *
 * Must be an object with properties 'key' (of type string)
 * and 'value' (of any type)
 *
 * @api private
 * @param {*} item
 * @return {Boolean}
 */

function isMixed(item) {
  if (!isObject(item)) {
    return false;
  }
  if (!isString(item.key)) {
    return false;
  }
  if (!has.call(item, "value")) {
    return false;
  }
  return true;
}

/**
 * TODO: Document me
 *
 * @api private
 * @param {Object} attrs
 * @param {Function} fn
 * @return {Image}
 */

function loadImage(attrs, fn) {
  fn = fn || function() {};
  const img = new Image();
  img.onerror = error(fn, "failed to load pixel", img);
  img.onload = function() {
    fn();
  };
  img.src = attrs.src;
  img.width = 1;
  img.height = 1;
  return img;
}

/**
 * TODO: Document me
 *
 * @api private
 * @param {Function} fn
 * @param {string} message
 * @param {Element} img
 * @return {Function}
 */

function error(fn, message, img) {
  return function(e) {
    e = e || window.event;
    const err = new Error(message);
    err.event = e;
    err.source = img;
    fn(err);
  };
}

/**
 * Render template + locals into an `attrs` object.
 *
 * @api private
 * @param {Object} template
 * @param {Object} locals
 * @return {Object}
 */

function render(template, locals) {
  return template(locals);
}

function slug(name) {
  return name.replace(/^ +| +$/g, "").replace(/ +/g, "-");
}

/**
 * Create a new `Integration` constructor.
 *
 * @constructs Integration
 * @param {string} name
 * @return {Function} Integration
 */

function createIntegration(name) {
  class Integration extends Emitter {
    /**
     * Initialize a new `Integration`.
     *
     * @class
     * @param {Object} options
     */
    constructor(options) {
      super();
      // if (options && options.addIntegration) {
      //  // plugin
      //  return options.addIntegration(Integration);
      // }
      this.debug = debug(`analytics:integration:${slug(name)}`);
      this.options = { ...this.defaults, ...(options || {}) };
      this._queue = [];
      this.once("ready", () => this.flush());

      // Integration.emit('construct', this);
      this._wrapInitialize();
      this._wrapPage();
    }

    /**
     * Add a new option to the integration by `key` with default `value`.
     *
     * @api public
     * @param {string} key
     * @param {*} value
     * @return {Integration}
     */
    static option(key, value) {
      this.prototype.defaults[key] = value;
      return this;
    }

    /**
     * Add a new mapping option.
     *
     * This will create a method `name` that will return a mapping for you to use.
     *
     * @api public
     * @param {string} name
     * @return {Integration}
     * @example
     * Integration('My Integration')
     *   .mapping('events');
     *
     * new MyIntegration().track('My Event');
     *
     * .track = function(track){
     *   var events = this.events(track.event());
     *   each(send, events);
     *  };
     */
    static mapping(name) {
      this.option(name, []);
      this.prototype[name] = function(key) {
        return this.map(this.options[name], key);
      };
      return this;
    }

    /**
     * Register a new global variable `key` owned by the integration, which will be
     * used to test whether the integration is already on the page.
     *
     * @api public
     * @param {string} key
     * @return {Integration}
     */
    static global(key) {
      this.prototype.globals.push(key);
      return this;
    }

    /**
     * Mark the integration as assuming an initial pageview, so to defer the first page call, keep track of
     * whether we already nooped the first page call.
     *
     * @api public
     * @return {Integration}
     */
    static assumesPageview() {
      this.prototype._assumesPageview = true;
      return this;
    }

    /**
     * Mark the integration as being 'ready' once `load` is called.
     *
     * @api public
     * @return {Integration}
     */
    static readyOnLoad() {
      this.prototype._readyOnLoad = true;
      return this;
    }

    /**
     * Mark the integration as being 'ready' once `initialize` is called.
     *
     * @api public
     * @return {Integration}
     */
    static readyOnInitialize() {
      this.prototype._readyOnInitialize = true;
      return this;
    }

    /**
     * Define a tag to be loaded.
     *
     * @api public
     * @param {string} [name='library'] A nicename for the tag, commonly used in
     * #load. Helpful when the integration has multiple tags and you need a way to
     * specify which of the tags you want to load at a given time.
     * @param {(opts) => {type: string, attrs: {[key: string]: string}}}} A method that returns a dom tag as object
     * @return {Integration}
     */
    static tag(name, tag) {
      if (tag == null) {
        tag = name;
        name = "library";
      }
      this.prototype.templates[name] = tag;
      return this;
    }

    /**
     * Initialize.
     */
    initialize() {
      const ready = this.ready;
      nextTick(ready);
    }

    /**
     * Loaded?
     *
     * @api private
     * @return {boolean}
     */
    loaded() {
      return false;
    }

    /**
     * Page.
     *
     * @api public
     * @param {Page} page
     */

    /* eslint-disable no-unused-vars */
    page(page) {}
    /* eslint-enable no-unused-vars */

    /**
     * Track.
     *
     * @api public
     * @param {Track} track
     */

    /* eslint-disable no-unused-vars */
    track(track) {}
    /* eslint-enable no-unused-vars */

    /**
     * Get values from items in `options` that are mapped to `key`.
     * `options` is an integration setting which is a collection
     * of type 'map', 'array', or 'mixed'
     *
     * Use cases include mapping events to pixelIds (map), sending generic
     * conversion pixels only for specific events (array), or configuring dynamic
     * mappings of event properties to query string parameters based on event (mixed)
     *
     * @api public
     * @param {Object|Object[]|String[]} options An object, array of objects, or
     * array of strings pulled from settings.mapping.
     * @param {string} key The name of the item in options whose metadata
     * we're looking for.
     * @return {Array} An array of settings that match the input `key` name.
     * @example
     *
     * // 'Map'
     * var events = { my_event: 'a4991b88' };
     * .map(events, 'My Event');
     * // => ['a4991b88']
     * .map(events, 'whatever');
     * // => []
     *
     * // 'Array'
     * * var events = ['Completed Order', 'My Event'];
     * .map(events, 'My Event');
     * // => ['My Event']
     * .map(events, 'whatever');
     * // => []
     *
     * // 'Mixed'
     * var events = [{ key: 'my event', value: '9b5eb1fa' }];
     * .map(events, 'my_event');
     * // => ['9b5eb1fa']
     * .map(events, 'whatever');
     * // => []
     */
    map(options, key) {
      const normalizedComparator = normalize(key);
      const mappingType = getMappingType(options);

      if (mappingType === "unknown") {
        return [];
      }

      const matchingValues = [];

      for (const innerKey in options) {
        if (!options.hasOwnProperty(innerKey)) {
          continue;
        }

        const val = options[innerKey];

        var compare;
        let result = val;

        if (mappingType === "map") {
          compare = innerKey;
        }

        if (mappingType === "array") {
          compare = val;
        }

        if (mappingType === "mixed") {
          compare = val.key;
          result = val.value;
        }

        if (normalize(compare) === normalizedComparator) {
          matchingValues.push(result);
        }
      }

      return matchingValues;
    }

    /**
     * Invoke a `method` that may or may not exist on the prototype with `args`,
     * queueing or not depending on whether the integration is 'ready'. Don't
     * trust the method call, since it contains integration party code.
     *
     * @api private
     * @param {string} method
     * @param {...*} args
     */
    invoke(method) {
      if (!this[method]) {
        return;
      }
      const args = Array.prototype.slice.call(arguments, 1);
      if (!this._ready) {
        return this.queue(method, args);
      }

      this.debug("%s with %o", method, args);
      return this[method].apply(this, args);
    }

    /**
     * Queue a `method` with `args`.
     *
     * @api private
     * @param {string} method
     * @param {Array} args
     */
    queue(method, args) {
      this._queue.push({ method, args });
    }

    /**
     * Flush the internal queue.
     *
     * @api private
     */
    flush() {
      this._ready = true;

      this._queue.forEach(call => {
        this[call.method].apply(this, call.args);
      });

      // Empty the queue.
      this._queue.length = 0;
    }

    /**
     * Reset the integration, removing its global variables.
     *
     * @api private
     */
    reset() {
      for (let i = 0; i < this.globals.length; i++) {
        window[this.globals[i]] = undefined;
      }

      window.onerror = onerror;
      window.onload = onload;
    }

    /**
     * Load a tag by `name`.
     *
     * @param {string} name The name of the tag.
     * @param {Object} locals Locals used to populate the tag's template variables
     * (e.g. `userId` in '<img src='https://whatever.com/{{ userId }}'>').
     * @param {Function} [callback=noop] A callback, invoked when the tag finishes
     * loading.
     */
    load(name, locals, callback) {
      // Argument shuffling
      if (typeof name === "function") {
        callback = name;
        locals = null;
        name = null;
      }
      if (name && typeof name === "object") {
        callback = locals;
        locals = name;
        name = null;
      }
      if (typeof locals === "function") {
        callback = locals;
        locals = null;
      }

      // Default arguments
      name = name || "library";
      locals = locals || {};

      locals = this.locals(locals);
      const template = this.templates[name];
      if (!template) {
        throw new Error(`template '${name}' not defined.`);
      }
      const { type, attrs } = render(template, locals);
      callback = callback || noop;
      const self = this;
      let el;

      switch (type) {
        case "img":
          attrs.width = 1;
          attrs.height = 1;
          el = loadImage(attrs, callback);
          break;
        case "script":
          el = loadScript(attrs, err => {
            if (!err) {
              return callback();
            }
            self.debug('error loading "%s" error="%s"', self.name, err);
          });

          break;
        default:
        // No default case
      }

      return el;
    }

    /**
     * Locals for tag templates.
     *
     * By default it includes a cache buster and all of the options.
     *
     * @param {Object} [locals]
     * @return {Object}
     */
    locals(locals) {
      locals = locals || {};
      const cache = Math.floor(new Date().getTime() / 3600000);

      return { ...this.options, cache, ...locals };
    }

    /**
     * Simple way to emit ready.
     *
     * @api public
     */
    ready = () => {
      this.emit("ready");
    };

    /**
     * Wrap the initialize method in an exists check, so we don't have to do it for
     * every single integration.
     *
     * @api private
     */
    _wrapInitialize() {
      const initialize = this.initialize;
      this.initialize = function() {
        this.debug("initialize");
        this._initialized = true;
        const ret = initialize.apply(this, arguments);
        this.emit("initialize");
        return ret;
      };
    }

    /**
     * Wrap the page method to call to noop the first page call if the integration assumes
     * a pageview.
     *
     * @api private
     */
    _wrapPage() {
      // Noop the first page call if integration assumes pageview
      if (this._assumesPageview) {
        const oldPage = this.page;
        this.page = function() {
          this.page = oldPage;
        };
      }

      return this.page;
    }
  }

  // This has to be defined like this as the static initialization functions
  // will override the integration's prototype before it's initialized
  Integration.prototype.defaults = {};
  Integration.prototype.globals = [];
  Integration.prototype.templates = {};
  Integration.prototype.name = name;

  return Integration;
}

/**
 * Exports.
 */

export default createIntegration;
